import { webPath } from "@pontimed/types";
import { Capacitor } from "@capacitor/core";

/**
 * Resolve the canonical `apps/web` marketing origin. Read from
 * `NEXT_PUBLIC_WEB_URL` (architecture rule 1 — same convention as
 * `NEXT_PUBLIC_API_URL`); fall back to `VITE_WEB_URL` for Vite-native dev
 * `.env.local` files; default to production `https://pontimed.app` if unset.
 */
export const WEB_URL: string =
  import.meta.env.NEXT_PUBLIC_WEB_URL ?? import.meta.env.VITE_WEB_URL ?? "https://pontimed.app";

/**
 * Hostname derived from `WEB_URL` — used as the visible label for marketing
 * links so dev builds show `pontimed.local` and prod shows `pontimed.app`,
 * matching the actual destination instead of a hardcoded brand string.
 */
export const WEB_HOST: string = (() => {
  try {
    return new URL(WEB_URL).host;
  } catch {
    return "pontimed.app";
  }
})();

/**
 * Build an absolute apps/web URL for cross-domain navigation from the portal
 * SPA into apps/web (marketing, legal, blog, help). Path format:
 * packages/types/src/web-path.ts — `/{country}[/{locale}]/{path}`.
 *
 * - `path` may begin with `/` or not, and may carry `?query` / `#hash`.
 * - `country` defaults to "ro" (matches `apps/web` `DEFAULT_COUNTRY`); use the
 *   user's resolved country once the portal has a country context.
 */
export function buildWebUrl(path: string, locale: string, country = "ro"): string {
  const base = WEB_URL.replace(/\/+$/, "");
  return `${base}${webPath(country, locale, path)}`;
}

/**
 * Canonical portal origin for links that leave the app — e.g. the "manage
 * this appointment" link inside an exported calendar event. Read from
 * `NEXT_PUBLIC_PORTAL_URL` / `VITE_PORTAL_URL`; otherwise the live origin when
 * it is a web origin (previews + local dev), never a Capacitor native shell
 * (`capacitor://localhost` on iOS, `https://localhost` on Android); finally
 * the production portal host.
 */
export const PORTAL_URL: string = (() => {
  const fromEnv = import.meta.env.NEXT_PUBLIC_PORTAL_URL ?? import.meta.env.VITE_PORTAL_URL;
  if (fromEnv) return String(fromEnv).replace(/\/+$/, "");
  if (
    typeof window !== "undefined" &&
    !Capacitor.isNativePlatform() &&
    /^https?:$/.test(window.location.protocol)
  ) {
    return window.location.origin;
  }
  return "https://portal.pontimed.app";
})();
